/**
 * Why the total moved after the customer was shown it.
 *
 * Every way it can move ends up here, and all of them before any charge: the
 * server refuses a stale total with 409, the app asks again, and this names the
 * reason in a sentence while the cart shows the old total struck through beside
 * the new one. A reason nobody can act on is still worth one line — a price that
 * changes without a word reads as a trick.
 */

import { Info } from "lucide-react";
import styled from "styled-components";
import { FEE, SAVINGS } from "./savings";

export type Notice = {
  /**
   * - gone: the nearby discount no longer applies to this time
   * - fee: a travel fee now applies
   * - zip: the address is in a different ZIP than the one typed
   * - prices: an admin changed prices during checkout
   * - changed: the total moved for another reason
   * - checked: the server refused, the app checked again, and nothing moved
   * - mismatch: the app still cannot reach the server's total
   */
  reason: "gone" | "fee" | "zip" | "prices" | "changed" | "checked" | "mismatch";
  wasTotalCents: number;
  zip?: string;
  fromZip?: string;
};

/** Amber when the total went up, green when it went down, plain otherwise. */
const TONES = {
  fee: FEE,
  save: SAVINGS,
  plain: { ink: "#101322", tint: "#fbfcff", edge: "#e7e9f3" },
};

const Box = styled.div<{ $tone: keyof typeof TONES }>`
  display: flex;
  gap: 12px;
  margin-bottom: 18px;
  padding: 14px 16px;
  background: ${(p) => TONES[p.$tone].tint};
  border: 1px solid ${(p) => TONES[p.$tone].edge};
  border-radius: 14px;

  svg {
    flex: 0 0 auto;
    margin-top: 1px;
    color: ${(p) => TONES[p.$tone].ink};
  }

  strong {
    display: block;
    font-size: 14px;
    font-weight: 600;
    color: ${(p) => TONES[p.$tone].ink};
  }

  p {
    margin: 4px 0 0;
    font-size: 13px;
    line-height: 1.55;
    color: #575e75;
  }

  button {
    margin-top: 6px;
    padding: 0;
    border: 0;
    background: none;
    color: var(--blue-300);
    font: inherit;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
  }
`;

function copy(notice: Notice): { title: string; body: string } {
  switch (notice.reason) {
    case "gone":
      return {
        title: "The nearby discount is gone for this time",
        body: "We no longer have a cleaning near you at this time. Here is your new total.",
      };
    case "fee":
      return {
        title: "A travel fee now applies",
        body: "We have no other cleaning near you at this time. Here is your new total.",
      };
    case "zip":
      return {
        title: `Your address is in ${notice.zip}`,
        body: `You started with ${notice.fromZip}, so we checked the price again for ${notice.zip}. Here is your new total.`,
      };
    case "prices":
      return {
        title: "Our prices changed while you were booking",
        body: "Here is the current total.",
      };
    case "checked":
      return {
        title: "We checked your price again",
        body: "It has not changed. Book again when you’re ready.",
      };
    case "mismatch":
      return {
        title: "We couldn’t confirm your total",
        body: "Please pick your date and time again, then book.",
      };
    default:
      return {
        title: "Your total changed",
        body: "We checked your area again. Here is your new total.",
      };
  }
}

export function PriceNotice({
  notice,
  currentTotalCents,
  pending,
  onRepick,
}: {
  notice: Notice | null;
  currentTotalCents: number;
  /** A quote is still on its way, so the total is not final yet. */
  pending?: boolean;
  onRepick: () => void;
}) {
  if (!notice) return null;
  // A different ZIP that prices the same is not news — and until the new ZIP's
  // quote is in, there is nothing to say yet: the total shown would be wrong.
  if (notice.reason === "zip" && (pending || notice.wasTotalCents === currentTotalCents)) {
    return null;
  }

  const { title, body } = copy(notice);
  const tone =
    notice.reason === "checked" || notice.reason === "mismatch"
      ? "plain"
      : currentTotalCents > notice.wasTotalCents
      ? "fee"
      : currentTotalCents < notice.wasTotalCents
      ? "save"
      : "plain";
  // Said only after a refused payment; before one, nothing was ever going to be charged.
  const afterRefusal = notice.reason !== "zip";
  return (
    <Box role="alert" $tone={tone}>
      <Info size={18} aria-hidden="true" />
      <div>
        <strong>{title}</strong>
        <p>
          {body}
          {afterRefusal ? " You have not been charged." : ""}
        </p>
        {notice.reason === "gone" || notice.reason === "mismatch" ? (
          <button type="button" onClick={onRepick}>
            Pick another time
          </button>
        ) : null}
      </div>
    </Box>
  );
}
